import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Heart, User, ShoppingCart, ChevronDown, Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';

const Header = () => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <>
      {/* Top Announcement Bar */}
      <div className="bg-[#1e3a5f] text-white text-center py-2 px-4 text-sm">
        Book a consultation with one of our design experts!
      </div>

      {/* Social and Contact Bar */}
      <div className="border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-2 flex justify-between items-center text-sm">
          <div className="flex items-center gap-4">
            <a href="tel:214-952-0001" className="text-gray-600 hover:text-gray-900 transition-colors">214-952-0001</a>
            <a href="mailto:dqkyukim@gmail.com" className="text-gray-600 hover:text-gray-900 transition-colors">dqkyukim@gmail.com</a>
          </div>
          <div className="flex items-center gap-4">
            <button className="text-gray-600 hover:text-gray-900 transition-colors flex items-center gap-1">
              United States (USD $)
              <ChevronDown size={14} />
            </button>
            <Heart size={18} className="text-gray-600 hover:text-gray-900 cursor-pointer transition-colors" />
            <User size={18} className="text-gray-600 hover:text-gray-900 cursor-pointer transition-colors" />
            <div className="relative">
              <ShoppingCart size={18} className="text-gray-600 hover:text-gray-900 cursor-pointer transition-colors" />
              <span className="absolute -top-2 -right-2 bg-gray-900 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">0</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header className="bg-white sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between py-4">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <div className="w-8 h-8 relative">
                <svg viewBox="0 0 100 100" className="w-full h-full">
                  <polygon points="50,10 90,30 90,70 50,90 10,70 10,30" fill="#1e3a5f" />
                  <polygon points="50,25 75,37.5 75,62.5 50,75 25,62.5 25,37.5" fill="white" />
                </svg>
              </div>
              <span className="text-xl font-light tracking-wider text-gray-800">DIAMOND Q JEWELERS</span>
            </Link>

            {/* Navigation */}
            <nav className="hidden lg:flex items-center gap-8">
              <div 
                className="relative group"
                onMouseEnter={() => setOpenDropdown('engagement')}
                onMouseLeave={() => setOpenDropdown(null)}
              >
                <button className="text-gray-700 hover:text-gray-900 transition-colors flex items-center gap-1 py-2">
                  Engagement
                  <ChevronDown size={16} />
                </button>
                {openDropdown === 'engagement' && (
                  <div className="absolute top-full left-0 bg-white shadow-lg rounded-md py-2 w-48 z-50">
                    <Link to="/engagement-rings" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">Engagement Rings</Link>
                    <Link to="/custom-design" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">Custom Design</Link>
                  </div>
                )}
              </div>
              
              <Link to="/wedding-bands" className="text-gray-700 hover:text-gray-900 transition-colors py-2">
                Wedding Bands
              </Link>
              
              <Link to="/lifestyle" className="text-gray-700 hover:text-gray-900 transition-colors py-2">
                Lifestyle
              </Link>
              
              <Link to="/in-stock" className="text-gray-700 hover:text-gray-900 transition-colors py-2">
                In Stock
              </Link>
              
              <div 
                className="relative group"
                onMouseEnter={() => setOpenDropdown('more')}
                onMouseLeave={() => setOpenDropdown(null)}
              >
                <button className="text-gray-700 hover:text-gray-900 transition-colors flex items-center gap-1 py-2">
                  More
                  <ChevronDown size={16} />
                </button>
                {openDropdown === 'more' && (
                  <div className="absolute top-full left-0 bg-white shadow-lg rounded-md py-2 w-48 z-50">
                    <Link to="/services" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">Services</Link>
                    <Link to="/about" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">About Us</Link>
                    <Link to="/contact" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">Contact</Link>
                  </div>
                )}
              </div>
            </nav>

            {/* Mobile Menu Button */}
            <button 
              className="lg:hidden text-gray-700"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>

            {/* Search */}
            <div className="flex items-center">
              {isSearchOpen ? (
                <div className="flex items-center gap-2">
                  <Input
                    type="text"
                    placeholder="Search"
                    className="w-64"
                    autoFocus
                  />
                  <Button variant="ghost" size="sm" onClick={() => setIsSearchOpen(false)}>
                    Cancel
                  </Button>
                </div>
              ) : (
                <Button variant="ghost" size="icon" onClick={() => setIsSearchOpen(true)} className="hidden lg:flex">
                  <Search size={20} />
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-b border-gray-200 shadow-lg">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <nav className="flex flex-col space-y-4">
              <Link 
                to="/engagement-rings" 
                className="text-gray-700 hover:text-gray-900 py-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Engagement Rings
              </Link>
              <Link 
                to="/wedding-bands" 
                className="text-gray-700 hover:text-gray-900 py-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Wedding Bands
              </Link>
              <Link 
                to="/lifestyle" 
                className="text-gray-700 hover:text-gray-900 py-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Lifestyle
              </Link>
              <Link 
                to="/in-stock" 
                className="text-gray-700 hover:text-gray-900 py-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                In Stock
              </Link>
              <Link 
                to="/custom-design" 
                className="text-gray-700 hover:text-gray-900 py-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Custom Design
              </Link>
              <Link 
                to="/services" 
                className="text-gray-700 hover:text-gray-900 py-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Services
              </Link>
              <Link 
                to="/about" 
                className="text-gray-700 hover:text-gray-900 py-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                About Us
              </Link>
              <Link 
                to="/contact" 
                className="text-gray-700 hover:text-gray-900 py-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Contact
              </Link>
            </nav>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;